import { useEffect, useRef, useState } from "react";
import type { FieldContent } from "@capture/shared";
import { useFormConfigStore } from "../../state/store";
import { blurOnEnter } from "./Field";

/**
 * Dropdown options editor — inline list of `{ value, label }` pairs
 * for `dataType=dropdown` fields. Renders only when the dispatcher
 * (TypeSpecificSection) selects this branch.
 *
 * Persistence model — every interaction (add / remove / commit edit)
 * writes the WHOLE updated `dropdownOptions` array to the store via
 * `updateField`. There is no separate save button — these options are
 * an integral part of the field's config.
 *
 * Validation:
 *   - Empty/whitespace value → row keeps prior value, inline error.
 *   - Duplicate value (against any OTHER row's value) → row keeps prior
 *     value, inline error. Self-equality (no-op edit) is silently
 *     accepted.
 *
 * The label is free-form — it's the operator-facing display string and
 * has no uniqueness constraint, so it commits on blur with no
 * validation surface.
 *
 * Stable keys — the wire shape `{ value, label }` carries no row id, so
 * React keys would default to array index. Index keys cause local-draft
 * bleed when a non-tail row is removed (the row that shifts up keeps
 * the deleted row's component instance + its in-flight draft state).
 * To avoid extending the persisted shape with a synthetic id (Option b
 * in the quality review — broader blast radius across snapshot-builder
 * + legacy-shim), we hold a parallel `string[]` of UUIDs in a `useRef`
 * and mutate it in lockstep with every persist call. Add appends a
 * fresh UUID; remove splices at the same index; rebuild from scratch
 * when the field selection changes (different field = different option
 * list, no draft preservation expected).
 */

type Option = NonNullable<FieldContent["dropdownOptions"]>[number];

export function DropdownOptionsSection() {
  const content = useFormConfigStore((s) => s.content);
  const selectedFieldName = useFormConfigStore((s) => s.selectedFieldName);
  const updateField = useFormConfigStore((s) => s.updateField);

  const field = content?.fields.find((f) => f.name === selectedFieldName) ?? null;
  const options: Option[] = field?.dropdownOptions ?? [];

  // Per-row inline error message, keyed by index. Only the value input
  // produces validation errors today (the label is free-form).
  const [rowErrors, setRowErrors] = useState<Record<number, string | undefined>>({});

  // Parallel UUID list for stable React keys — see file-level JSDoc.
  // Held in a ref + a render counter so add/remove can mutate it
  // synchronously alongside persist without a render-loop.
  const keysRef = useRef<string[]>([]);
  const fieldIdRef = useRef<string | undefined>(undefined);

  // Rebuild keys when the selected field changes (different option list
  // entirely — no carryover) OR when the option count drifts from the
  // key count (legacy snapshots loaded mid-session, etc.). Reused keys
  // preserve component identity across edits to value/label.
  if (field && (field.fieldDefinitionId !== fieldIdRef.current || keysRef.current.length !== options.length)) {
    keysRef.current = options.map((_, i) => keysRef.current[i] ?? crypto.randomUUID());
    fieldIdRef.current = field.fieldDefinitionId;
  }

  if (!field || !content) return null;

  function clearError(index: number) {
    setRowErrors((prev) => {
      if (prev[index] === undefined) return prev;
      const next = { ...prev };
      delete next[index];
      return next;
    });
  }

  function setError(index: number, message: string) {
    setRowErrors((prev) => ({ ...prev, [index]: message }));
  }

  function persist(next: Option[]) {
    if (!field) return;
    updateField(field.name, { dropdownOptions: next });
  }

  function addOption() {
    keysRef.current = [...keysRef.current, crypto.randomUUID()];
    persist([...options, { value: "", label: "" }]);
  }

  function removeOption(index: number) {
    keysRef.current = keysRef.current.filter((_, i) => i !== index);
    persist(options.filter((_, i) => i !== index));
    // Reindex errors so a row's error doesn't follow it to a deleted slot.
    setRowErrors((prev) => {
      const next: Record<number, string | undefined> = {};
      for (const [k, v] of Object.entries(prev)) {
        const ki = Number(k);
        if (ki < index) next[ki] = v;
        else if (ki > index) next[ki - 1] = v;
        // ki === index is dropped
      }
      return next;
    });
  }

  function commitValue(index: number, draft: string) {
    const trimmed = draft.trim();
    const current = options[index];
    if (!current) return;

    if (trimmed === current.value) {
      clearError(index);
      return;
    }
    if (trimmed === "") {
      setError(index, "Value is required.");
      return;
    }
    if (options.some((opt, i) => i !== index && opt.value === trimmed)) {
      setError(index, "Value must be unique.");
      return;
    }
    clearError(index);
    persist(options.map((opt, i) => (i === index ? { ...opt, value: trimmed } : opt)));
  }

  function commitLabel(index: number, draft: string) {
    const trimmed = draft.trim();
    const current = options[index];
    if (!current || trimmed === current.label) return;
    persist(options.map((opt, i) => (i === index ? { ...opt, label: trimmed } : opt)));
  }

  return (
    <section className="field-section" aria-label="Dropdown Options">
      <h3 className="field-section__title">Dropdown Options</h3>

      {options.length === 0 ? (
        <p className="field-section__hint">No options yet. Add one to get started.</p>
      ) : (
        <ul className="field-section__list">
          {options.map((opt, index) => (
            <DropdownOptionRow
              key={keysRef.current[index] ?? index}
              index={index}
              option={opt}
              error={rowErrors[index]}
              onCommitValue={commitValue}
              onCommitLabel={commitLabel}
              onRemove={removeOption}
            />
          ))}
        </ul>
      )}

      <button type="button" className="field-section__button" onClick={addOption}>
        + Add option
      </button>
    </section>
  );
}

interface DropdownOptionRowProps {
  index: number;
  option: Option;
  error: string | undefined;
  onCommitValue: (index: number, draft: string) => void;
  onCommitLabel: (index: number, draft: string) => void;
  onRemove: (index: number) => void;
}

function DropdownOptionRow({
  index,
  option,
  error,
  onCommitValue,
  onCommitLabel,
  onRemove,
}: DropdownOptionRowProps) {
  // Local drafts so the input shows what the user is typing while we
  // wait for blur to validate and commit.
  const [valueDraft, setValueDraft] = useState(option.value);
  const [labelDraft, setLabelDraft] = useState(option.label);

  // Reset drafts when the underlying option changes from the store
  // (e.g., a successful commit, or another row's add/remove shuffled
  // indexes). Mirrors the IdentitySection effect pattern.
  useEffect(() => {
    setValueDraft(option.value);
  }, [option.value]);
  useEffect(() => {
    setLabelDraft(option.label);
  }, [option.label]);

  const valueId = `dropdown-option-value-${index}`;
  const labelId = `dropdown-option-label-${index}`;

  return (
    <li className="field-section__list-row">
      <div className="field-section__list-inputs">
        <label htmlFor={valueId} className="field-section__label">
          Option value
        </label>
        <input
          id={valueId}
          type="text"
          className="field-section__input"
          value={valueDraft}
          onChange={(e) => setValueDraft(e.target.value)}
          onBlur={() => onCommitValue(index, valueDraft)}
          onKeyDown={blurOnEnter}
        />

        <label htmlFor={labelId} className="field-section__label">
          Option label
        </label>
        <input
          id={labelId}
          type="text"
          className="field-section__input"
          value={labelDraft}
          onChange={(e) => setLabelDraft(e.target.value)}
          onBlur={() => onCommitLabel(index, labelDraft)}
          onKeyDown={blurOnEnter}
        />
      </div>

      <button
        type="button"
        className="field-section__icon-button"
        aria-label="Remove option"
        onClick={() => onRemove(index)}
      >
        ×
      </button>

      {error && (
        <div className="field-section__error" role="alert">
          {error}
        </div>
      )}
    </li>
  );
}
